import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown } from 'lucide-react';
import styles from '../css/drugSearch.module.css';
import { searchMedications } from '../../../../api/dispensary/script/medicationCardApi.js';
import { searchReceivedStock } from '../../../../api/Inventory/RecieveStockApi.js';

// Clean version of the medication search input for Receive Stock
// Magnifying icon on the left, caret on the right, scrollable dropdown on type.
export default function DrugSearchInputClean({
  id,
  initialValue = '',
  placeholder = 'Select medication',
  numericOnly = false,
  onSelect,
  productType,   // optional: 'Drug' | 'Item' for Receive Stock search
  searchBackend, // optional override search function(term) => results
  department,    // optional department/tab name for receive stock
  getDisplayValue, // optional function(med) => string for input display
  onInputChange,  // optional: called with current term on every manual change
  size = 'default', // new prop: 'default' | 'small' | 'large'
  noResultsMessage = 'No medications found', // customizable no results message
  borderRadius = '4px', // new prop: border radius for rounded corners
  length, // new prop: custom width for the input
  arrowInside = false, // new prop: move arrow inside the input
  dropdownOnly = false, // new prop: behave like traditional dropdown - collapsed by default, only show options when searching with results
  openDropdownOnFocus = false,
  minSearchLength = 2
}) {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);

  // Size-based CSS classes
  const sizeClasses = {
    default: styles.inputDefault,
    small: styles.inputSmall,
    large: styles.inputLarge
  };

  // Arrow inside size classes
  const arrowInsideSizeClasses = {
    default: styles.inputArrowInsideDefault,
    small: styles.inputArrowInsideSmall,
    large: styles.inputArrowInsideLarge
  };

  const currentSizeClass = sizeClasses[size] || sizeClasses.default;
  const currentArrowInsideSizeClass = arrowInsideSizeClasses[size] || arrowInsideSizeClasses.default;

  useEffect(() => {
    setSearchTerm(initialValue || '');
  }, [initialValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearch = async (rawTerm) => {
    const term = numericOnly ? String(rawTerm || '').replace(/[^0-9]/g, '') : rawTerm;
    
    // For dropdownOnly mode, always hide dropdown if search term is too short
    if (term.length < minSearchLength) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    try {
      let results;

      if (typeof searchBackend === 'function') {
        results = await searchBackend(term);
      } else if (productType === 'Drug' || productType === 'Item') {
        const data = await searchReceivedStock(productType, term, department);
        results = data ? [data] : [];
      } else {
        const meds = await searchMedications({ search: term });
        results = meds;
      }

      const finalResults = Array.isArray(results) ? results : [];
      setSearchResults(finalResults);
      
      // For dropdownOnly mode, only show dropdown if there are results
      // For normal mode, show dropdown with "No results" message if no results
      if (dropdownOnly) {
        setShowDropdown(finalResults.length > 0);
      } else {
        setShowDropdown(true);
      }
    } catch (error) {
      console.error('Error searching medications:', error);
      setSearchResults([]);
      setShowDropdown(false);
    } finally {
      setIsSearching(false);
    }
  };

  const handleInputChange = (e) => {
    const raw = e.target.value;
    const term = numericOnly ? raw.replace(/[^0-9]/g, '') : raw;
    setSearchTerm(term);

    if (typeof onInputChange === 'function') {
      onInputChange(term);
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(term);
    }, 150);
  };

  const handleFocus = () => {
    if (openDropdownOnFocus) {
      handleSearch(searchTerm);
      return;
    }

    if (!dropdownOnly && searchTerm.length >= minSearchLength) {
      setShowDropdown(true);
    }
  };

  const handleSelect = (medication) => {
    const display = typeof getDisplayValue === 'function'
      ? getDisplayValue(medication) ?? ''
      : (medication?.name || '');
    setSearchTerm(display);
    setShowDropdown(false);
    setSearchResults([]);
    onSelect?.(medication);
  };

  return (
    <div className={styles.medSearchContainer} style={{ width: length || '100%' }}>
      {arrowInside ? (
        <div className={styles.arrowInsideContainer}>
          <Search className={styles.medIconLeft} size={size === 'small' ? 14 : size === 'large' ? 22 : 18} />
          <input
            id={id}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={handleFocus}
            className={`${styles.input} ${styles.medInput} ${currentSizeClass} ${currentArrowInsideSizeClass} ${styles.inputBase}`}
            placeholder={placeholder}
            autoComplete="off"
            style={{
              width: '100%',
              borderRadius: borderRadius
            }}
          />
          <ChevronDown 
            size={size === 'small' ? 14 : size === 'large' ? 22 : 18}
            className={`${styles.medCaretRight} ${styles.arrowInside}`}
          />
        </div>
      ) : (
        <>
          <Search className={styles.medIconLeft} size={size === 'small' ? 14 : size === 'large' ? 22 : 18} />
          <input
            id={id}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={handleFocus}
            className={`${styles.input} ${styles.medInput} ${currentSizeClass} ${styles.inputBase}`}
            placeholder={placeholder}
            autoComplete="off"
            style={{
              width: length || undefined,
              borderRadius: borderRadius
            }}
          />
          <ChevronDown className={styles.medCaretRight} size={size === 'small' ? 14 : size === 'large' ? 22 : 18} />
        </>
      )}
      {showDropdown && (
        <div className={styles.autocompleteDropdown}>
          {isSearching ? (
            <div className={styles.dropdownItem}>Searching...</div>
          ) : searchResults.length > 0 ? (
            searchResults.map((medication) => (
              <div
                key={medication.sku}
                className={styles.dropdownItem}
                onClick={() => handleSelect(medication)}
              >
                <div className={styles.medicationName}>{medication.name}</div>
                <div className={styles.medicationDetails}>
                  {medication.strength && `${medication.strength} `}
                  {medication.packSize && `• ${medication.packSize} `}
                  {medication.quantity && `• Stock: ${medication.quantity}`}
                </div>
              </div>
            ))
          ) : !dropdownOnly ? (
            <div className={styles.dropdownItem}>{noResultsMessage}</div>
          ) : null}
        </div>
      )}
    </div>
  );
}